import type { FC, ReactNode } from 'react'
import type { AuthorRef } from '@/types'

import Link from 'next/link'
import { cn } from '@/lib/utils'
import { Avatar } from '@/components/blog/avatar'
import { ReadingTime } from '@/components/blog/reading-time'

interface PostMetaProps {
    author: AuthorRef | null
    publishedAt: string | null
    publishedLabel: string
    readingTime: string
    size?: 'sm' | 'md'
    className?: string
}

export const PostMeta: FC<PostMetaProps> = ({
    author,
    publishedAt,
    publishedLabel,
    readingTime,
    size = 'sm',
    className
}): ReactNode => {
    return (
        <div className={cn('flex items-center gap-3', className)}>
            {author && (
                <Avatar
                    src={author.avatar}
                    name={author.name}
                    className={size === 'md' ? 'size-11 text-sm' : 'size-9 text-xs'}
                />
            )}

            <div className={cn('flex min-w-0 flex-col', size === 'md' ? 'gap-0.5 text-sm' : 'text-[0.8rem] leading-snug')}>
                {author && (
                    <Link
                        href={`/authors/${author.slug}`}
                        className='relative z-10 w-fit truncate font-medium text-foreground transition-colors hover:text-brand focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring'
                    >
                        {author.name}
                    </Link>
                )}

                <p className='flex flex-wrap items-center gap-x-1.5 text-muted-foreground'>
                    {publishedLabel && (
                        <time dateTime={publishedAt ?? undefined}>
                            {publishedLabel}
                        </time>
                    )}

                    {publishedLabel && <span aria-hidden='true'>&middot;</span>}

                    <ReadingTime value={readingTime} />
                </p>
            </div>
        </div>
    )
}
